import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";

import { BucketKey } from "components/buckets/BucketToggle";
import { asUsDate } from "components/ui/formatters";
import { BucketTotals } from "helpers/types";
import { BucketTwrFormula } from "constants/index";
import { chartsReportStyles } from "styles/ChartsReport.styles";
import BucketSeriesSelect, { BucketSeriesSection } from "./BucketSeriesSelect";
import PerformanceLineChart from "./PerformanceLineChart";
import {
  ALL_SELECTIONS,
  BucketSelection,
  ChartSelections,
  ChartSpec,
  EMPTY_SELECTION,
  buildChartSpecs,
  bucketSecurities,
  hasBucketData,
  hasStocksSeries,
  selectedSecurities,
} from "./chartSeriesSelection";

/**
 * The one chart's heading. It names the section rather than a series — every
 * selected series is drawn on it, and the legend beneath names each one.
 */
export const PERFORMANCE_CHART_TITLE = "Bucket Performance";

/**
 * The one control carrying Bucket 2 and Bucket 3 together.
 *
 * Named rather than numbered because the two are read together: Bucket 3 IS
 * the tax-loss harvest sleeve (the Diversify card's own row for it is that
 * bucket's performance), and it carries no securities of its own, so a
 * drop-down to itself offered a single row.
 */
export const TAX_LOSS_HARVEST_TITLE = "Tax-Loss Harvest";

/**
 * How each control is made up: its title, the id its label element carries,
 * and the buckets it offers in menu order, each with the prefix its rows wear.
 *
 * The prefix is what still names a row's bucket once several buckets share a
 * menu — "B2: TSLA" — and is empty for a control that carries one bucket and
 * has already named it above the field. The CHART is unaffected: a line is
 * still titled "Bucket 2: TSLA" in the legend and the tooltip, where there is
 * room for the whole word.
 */
const SERIES_CONTROLS: {
  title: string;
  controlId: string;
  buckets: { bucketKey: BucketKey; prefix: string }[];
}[] = [
  {
    title: "Bucket 1",
    controlId: "bucket-1-series",
    buckets: [{ bucketKey: "1", prefix: "" }],
  },
  {
    title: TAX_LOSS_HARVEST_TITLE,
    controlId: "tax-loss-harvest-series",
    buckets: [
      { bucketKey: "2", prefix: "B2: " },
      { bucketKey: "3", prefix: "B3: " },
    ],
  },
];

export interface ChartsReportProps {
  /**
   * The calculated report's per-bucket data. null before the report has been
   * calculated, and reset to null whenever the account or the period changes:
   * the series belong to one run, so the previous run's charts must not stand
   * beside a new period's summary cards.
   */
  bucketTotals?: BucketTotals | null;
  /**
   * The report period, for the caption. Labelled as the PERIOD and not as
   * what the charts cover, because it is not: every series opens on its own
   * baseline, which is by construction a priced day strictly before the
   * start date, and ends on the last day its own inputs were priced.
   */
  period?: [string, string];
  /**
   * Which time-weighted return the Summary gear is set to, and so which of
   * the bucket lines this chart draws (operator, task 3764). The gear's pick
   * re-draws from the run already on screen -- every formula's line came
   * with it -- which is why this is a prop rather than another run.
   *
   * Optional, defaulting to the report's own default formula, so a caller
   * that has no gear draws exactly what it drew before.
   */
  twrFormula?: BucketTwrFormula;
  /**
   * Told the series the chart is drawing whenever they change, so the PDF
   * export can carry the chart exactly as it stands on screen -- the reader's
   * own selection lives here, not in the report.
   */
  onSpecsChange?: (specs: ChartSpec[]) => void;
}

/**
 * The Strategy Report's performance chart section: the series drop-downs, and
 * ONE chart carrying every series they select.
 *
 * OVERLAID, not one chart per selection. Every series is the same measurement
 * in the same unit — an index opening at 100% — so shared axes are what let
 * the reader compare them; stacked down the page, comparing two securities
 * meant reading two separate y-ranges against each other. The tooltip gives
 * the hovered day one row per line rather than the single series' figures.
 *
 * It OPENS on All Securities for every bucket, so the reader is shown the
 * three bucket lines without asking for them, and narrows from there.
 *
 * The series come from the calculated report, so there is nothing to plot
 * until Calculate has run. That is stated in place of the chart rather than
 * left as an empty plot area, which reads as a period in which nothing
 * happened.
 */
const ChartsReport = ({
  bucketTotals,
  period,
  twrFormula = BucketTwrFormula.DAILY_WEIGHT_ADJUSTMENT,
  onSpecsChange,
}: ChartsReportProps) => {
  const [selections, setSelections] =
    useState<ChartSelections>(ALL_SELECTIONS);

  const handleSelectionChange = useCallback(
    (bucketKey: BucketKey, next: BucketSelection) =>
      setSelections((current) => ({ ...current, [bucketKey]: next })),
    [],
  );

  // A bucket this run does not carry keeps whatever selection it was holding —
  // that is what puts it back on the chart when the next run does carry it
  // (see ALL_SELECTIONS) — but its rows are shown as UNTICKED. Reading "All
  // Securities" off a greyed row claims a selection nothing draws, and the
  // reader has no way to check it against a chart.
  const controls = useMemo(
    () =>
      SERIES_CONTROLS.map((control) => ({
        ...control,
        sections: control.buckets.map(({ bucketKey, prefix }) => {
          const disabled = !hasBucketData(bucketTotals, bucketKey);
          return {
            bucketKey,
            prefix,
            securities: bucketSecurities(bucketTotals, bucketKey),
            // Read off the run rather than asserted from the bucket number,
            // so the row is offered exactly where the payload carries a
            // series to draw (hasStocksSeries).
            offersStocks: hasStocksSeries(bucketTotals, bucketKey),
            selectedSecurities: selectedSecurities(
              bucketTotals,
              bucketKey,
              selections[bucketKey],
            ),
            selection: disabled ? EMPTY_SELECTION : selections[bucketKey],
            disabled,
          } satisfies BucketSeriesSection;
        }),
      })),
    [bucketTotals, selections],
  );

  const specs = useMemo(
    () => buildChartSpecs(bucketTotals, selections, twrFormula),
    [bucketTotals, selections, twrFormula],
  );

  useEffect(() => {
    onSpecsChange?.(specs);
  }, [specs, onSpecsChange]);

  const isCalculated = Boolean(bucketTotals);
  const caption =
    period?.[0] && period?.[1]
      ? `Report period: ${asUsDate(period[0])} – ${asUsDate(period[1])}`
      : null;

  return (
    <Box sx={chartsReportStyles.container}>
      <Box sx={chartsReportStyles.selectorRow}>
        {controls.map((control) => (
          <BucketSeriesSelect
            key={control.controlId}
            label={control.title}
            controlId={control.controlId}
            sections={control.sections}
            onChange={handleSelectionChange}
          />
        ))}
      </Box>

      {caption && specs.length > 0 && (
        <Typography sx={chartsReportStyles.subtitle}>{caption}</Typography>
      )}

      {specs.length > 0 ? (
        <PerformanceLineChart title={PERFORMANCE_CHART_TITLE} specs={specs} />
      ) : (
        <Box sx={chartsReportStyles.placeholder}>
          <Typography variant="body2">
            {isCalculated
              ? "Choose a bucket's securities above to chart their performance."
              : "Calculate the report to display each bucket's performance."}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ChartsReport;
